import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { fetchMainCategories } from "../../store/slices/mainCategorySlice";
import { fetchCategories } from "../../store/slices/categorySlice";
import { RootState, AppDispatch } from "../../store";
import {
  RiFacebookCircleFill,
  RiTwitterFill,
  RiYoutubeFill,
  RiInstagramLine,
  RiArrowRightLine,
  RiMailLine,
} from "react-icons/ri";
import {
  MdOutlineWorkOutline,
  MdOutlineStars,
  MdHelpOutline,
  MdKeyboardArrowDown,
} from "react-icons/md";
import {
  AiOutlineGift,
  AiOutlineSafetyCertificate,
  AiOutlineShoppingCart,
} from "react-icons/ai";

const sectionTitleStyle: React.CSSProperties = {
  color: "#fff",
  fontWeight: "700",
  fontSize: "13px",
  letterSpacing: "0.8px",
  textTransform: "uppercase",
  marginBottom: "16px",
  fontFamily: "'Outfit', 'Inter', sans-serif",
};

const footerLinkStyle: React.CSSProperties = {
  fontSize: "14px",
  cursor: "pointer",
  color: "#a0aec0",
  transition: "all 0.22s ease",
  textDecoration: "none",
  display: "block",
  padding: "4px 0",
  fontFamily: "'Outfit', 'Inter', sans-serif",
};

interface AccordionSectionProps {
  title: string;
  children: React.ReactNode;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  children,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid #2d3748" }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          background: "none",
          border: "none",
          padding: "20px 0",
          cursor: "pointer",
          textAlign: "left",
        }}
      >
        <span style={{ ...sectionTitleStyle, marginBottom: 0 }}>{title}</span>
        <MdKeyboardArrowDown
          style={{
            fontSize: "20px",
            color: "#a0aec0",
            transition: "transform 0.3s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </button>
      <div
        style={{
          maxHeight: open ? "400px" : "0",
          overflow: "hidden",
          transition: "all 0.35s cubic-bezier(0.4,0,0.2,1)",
          paddingBottom: open ? "20px" : "0",
          display: "flex",
          flexDirection: "column",
          gap: "8px",
        }}
      >
        {children}
      </div>
    </div>
  );
};

const Footer = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { mainCategories } = useSelector((state: RootState) => state.mainCategory);
  const { categories } = useSelector((state: RootState) => state.category);

  useEffect(() => {
    if (!mainCategories || mainCategories.length === 0) {
      dispatch(fetchMainCategories({ limit: 1000 }));
    }
    if (!categories || categories.length === 0) {
      dispatch(fetchCategories({ limit: 1000 }));
    }
  }, [dispatch, mainCategories, categories]);

  const displayMainCategories = [...mainCategories]
    .sort((a: any, b: any) => (Number(a.priority) || 999) - (Number(b.priority) || 999))
    .slice(0, 5);

  const displayCategories = [...categories]
    .sort((a: any, b: any) => (Number(a.priority) || 999) - (Number(b.priority) || 999))
    .slice(0, 5);

  return (
    <footer
      style={{
        backgroundColor: "#111827",
        color: "white",
        padding: "60px 0 0 0",
        marginTop: "60px",
        fontFamily: "'Outfit', 'Inter', sans-serif",
      }}
    >
      <style>{`
                .footer-link:hover { color: #dbeb83 !important; transform: translateX(5px); }
                .footer-social-circle {
                    width: 38px; height: 38px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.05);
                    display: flex; align-items: center; justify-content: center;
                    cursor: pointer;
                    transition: all 0.25s ease;
                    color: #a0aec0;
                }
                .footer-social-circle:hover { 
                    background: #dbeb83; 
                    color: #3d4000;
                    transform: translateY(-4px); 
                }
                
                .newsletter-input {
                    background: rgba(255,255,255,0.04);
                    border: 1.5px solid rgba(255,255,255,0.12);
                    color: white;
                    padding: 12px 18px;
                    border-radius: 12px;
                    width: 100%;
                    outline: none;
                    transition: all 0.3s;
                    font-size: 14px;
                    font-family: 'Outfit', sans-serif;
                }
                .newsletter-input:focus { 
                    border-color: var(--color-primary); 
                    background: rgba(255,255,255,0.07);
                    box-shadow: 0 0 0 4px rgba(0, 164, 95, 0.15);
                }
                
                .footer-grid {
                    display: grid;
                    grid-template-columns: 1.2fr 1fr 1fr 1fr 1.5fr;
                    gap: 30px;
                    margin-bottom: 50px;
                }

                .footer-link-bottom:hover {
                    color: #dbeb83 !important;
                }

                @media (max-width: 1024px) {
                    .footer-grid { grid-template-columns: repeat(3, 1fr); gap: 24px; }
                }

                @media (max-width: 900px) {
                    .footer-grid { display: none; }
                    .footer-mobile { display: block !important; }
                    .newsletter-section { margin-bottom: 40px; }
                }
            `}</style>

      <div style={{ maxWidth: "1600px", margin: "0 auto", padding: "0 40px" }}>
        {/* ── Desktop Layout ── */}
        <div className="footer-grid">
          {/* Brand & Newsletter */}
          <div style={{ gridColumn: "span 1" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                marginBottom: "24px",
              }}
            >
              <img
                src="/honeywill.png"
                alt="HoneyCool"
                style={{ height: "50px", width: "auto", borderRadius: "6px" }}
              />
            </div>
            <p
              style={{
                color: "#a0aec0",
                fontSize: "14px",
                lineHeight: "1.6",
                marginBottom: "24px",
              }}
            >
              Premium HVAC, Air Conditioning & Refrigeration solutions. Crafted with precision, engineered for performance.
            </p>
            <div style={{ display: "flex", gap: "12px" }}>
              {[
                RiFacebookCircleFill,
                RiTwitterFill,
                RiYoutubeFill,
                RiInstagramLine,
              ].map((Icon, i) => (
                <div key={i} className="footer-social-circle">
                  <Icon size={20} />
                </div>
              ))}
            </div>
          </div>

          {/* Main Categories Column */}
          <div>
            <div style={sectionTitleStyle}>Main Categories</div>
            {displayMainCategories.map((mc: any) => (
              <Link
                key={mc._id}
                to={`/products?main_category=${mc._id}`}
                className="footer-link"
                style={footerLinkStyle}
              >
                {mc.name}
              </Link>
            ))}
          </div>

          {/* Categories Column */}
          <div>
            <div style={sectionTitleStyle}>Categories</div>
            {displayCategories.map((cat: any) => (
              <Link
                key={cat._id}
                to={`/products?category=${cat._id}`}
                className="footer-link"
                style={footerLinkStyle}
              >
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Terms & Policies */}
          <div>
            <div style={sectionTitleStyle}>Terms & Policies</div>
            {[
              { label: "Terms & Conditions", path: "/terms-and-conditions" },
              { label: "Privacy Policy", path: "/privacy-policy" },
              { label: "FAQ", path: "/faq" },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.path}
                className="footer-link"
                style={footerLinkStyle}
              >
                {item.label}
              </Link>
            ))}
          </div>

          {/* Newsletter */}
          <div className="newsletter-section">
            <div style={sectionTitleStyle}>Join Our Newsletter</div>
            <p
              style={{
                color: "#a0aec0",
                fontSize: "14px",
                marginBottom: "20px",
              }}
            >
              Get the latest updates on new collections and exclusive offers.
            </p>
            <div style={{ position: "relative" }}>
              <input
                type="email"
                placeholder="Email Address"
                className="newsletter-input"
              />
              <button
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "var(--color-primary)",
                  border: "none",
                  borderRadius: "8px",
                  width: "36px",
                  height: "36px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "white",
                  transition: "all 0.2s",
                }}
              >
                <RiArrowRightLine size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* ── Mobile Layout ── */}
        <div className="footer-mobile" style={{ display: "none" }}>
          <div style={{ marginBottom: "40px", textAlign: "center" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                justifyContent: "center",
                marginBottom: "16px",
              }}
            >
              <img
                src="/honeywill.png"
                alt="HoneyCool"
                style={{ height: "50px", width: "auto", borderRadius: "8px" }}
              />
            </div>
            <p
              style={{
                color: "#a0aec0",
                fontSize: "14px",
                maxWidth: "300px",
                margin: "0 auto 24px auto",
              }}
            >
              Premium furniture and home decor for the modern living space.
            </p>
            <div
              style={{ display: "flex", gap: "16px", justifyContent: "center" }}
            >
              {[
                RiFacebookCircleFill,
                RiTwitterFill,
                RiYoutubeFill,
                RiInstagramLine,
              ].map((Icon, i) => (
                <div key={i} className="footer-social-circle">
                  <Icon size={20} />
                </div>
              ))}
            </div>
          </div>

          <div className="newsletter-section" style={{ marginBottom: "40px" }}>
            <div style={{ ...sectionTitleStyle, textAlign: "center" }}>
              Join Our Newsletter
            </div>
            <div style={{ position: "relative" }}>
              <input
                type="email"
                placeholder="Email Address"
                className="newsletter-input"
              />
              <button
                style={{
                  position: "absolute",
                  right: "8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "var(--color-primary)",
                  border: "none",
                  borderRadius: "8px",
                  width: "36px",
                  height: "36px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  color: "white",
                  transition: "all 0.2s",
                }}
              >
                <RiArrowRightLine size={20} />
              </button>
            </div>
          </div>

          <AccordionSection title="MAIN CATEGORIES">
            {displayMainCategories.map((mc: any) => (
              <Link
                key={mc._id}
                to={`/products?main_category=${mc._id}`}
                className="footer-link"
                style={footerLinkStyle}
              >
                {mc.name}
              </Link>
            ))}
          </AccordionSection>

          <AccordionSection title="CATEGORIES">
            {displayCategories.map((cat: any) => (
              <Link
                key={cat._id}
                to={`/products?category=${cat._id}`}
                className="footer-link"
                style={footerLinkStyle}
              >
                {cat.name}
              </Link>
            ))}
          </AccordionSection>

          <AccordionSection title="TERMS & POLICIES">
            {[
              { label: "Terms & Conditions", path: "/terms-and-conditions" },
              { label: "Privacy Policy", path: "/privacy-policy" },
              { label: "FAQ", path: "/faq" },
            ].map((item) => (
              <Link
                key={item.label}
                to={item.path}
                className="footer-link"
                style={footerLinkStyle}
              >
                {item.label}
              </Link>
            ))}
          </AccordionSection>
        </div>

        {/* ── Bottom Bar ── */}
        <div
          style={{
            borderTop: "1px solid #2d3748",
            padding: "30px 0",
            marginTop: "40px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "24px",
          }}
        >
          <div style={{ display: "flex", gap: "30px", flexWrap: "wrap" }}>
            {[
              { icon: MdOutlineWorkOutline, label: "Buy on HoneyCool" },
              { icon: MdHelpOutline, label: "Help Center" },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  fontSize: "13px",
                  color: "#a0aec0",
                  cursor: "pointer",
                  fontFamily: "'Outfit', sans-serif",
                }}
              >
                <Icon size={18} style={{ color: "var(--color-primary)" }} />
                <span
                  className="footer-link-bottom"
                  style={{ transition: "color 0.2s" }}
                >
                  {label}
                </span>
              </div>
            ))}
          </div>

          <div
            style={{
              fontSize: "13px",
              color: "#718096",
              fontFamily: "'Outfit', sans-serif",
            }}
          >
            © {new Date().getFullYear()} HoneyCool V1.0. All rights reserved.
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
